import React, { useCallback, useContext, useEffect } from "react";

import API from "../../../../../services/api";
import Banner from "../../../../atoms/banner/Banner";
import Form from "../../../../atoms/form/Form";
import VerticalGroup from "../../../../atoms/verticalgroup/VerticalGroup";
import { SimulationContext } from "../../context";
import useForm from "../../../../../hooks/useForm";
import Radio from "../../../../atoms/form/input/Radio";
import Checkbox from "../../../../atoms/form/input/Checkbox";
import Text from "../../../../atoms/text/Text";

export const getEnabledDivisions = (
  businessSetup: API.SimulationUpdateRequest["businessSetup"],
) => {
  const listOfDivisions: string[] = [];
  if (businessSetup?.homeLoansEnabled) {
    listOfDivisions.push("Home Loans");
  }
  if (businessSetup?.businessLoansEnabled) {
    listOfDivisions.push("Business Loans");
  }
  if (businessSetup?.depositsEnabled) {
    listOfDivisions.push("Deposits");
  }
  if (businessSetup?.creditCardsEnabled) {
    listOfDivisions.push("Credit Cards");
  }
  if (businessSetup?.wealthManagementEnabled) {
    listOfDivisions.push("Wealth Management");
  }
  if (businessSetup?.institutionalLendingEnabled) {
    listOfDivisions.push("Institutional Lending");
  }
  if (businessSetup?.offshoreBankEnabled) {
    listOfDivisions.push("Offshore Bank");
  }
  return listOfDivisions;
};

const Business: React.FC = () => {
  const { config, updateConfig, assignOnSubmitFunction, updateFormStatus } =
    useContext(SimulationContext);
  const [
    {
      formData: {
        homeLoansEnabled,
        businessLoansEnabled,
        depositsEnabled,
        creditCardsEnabled,
        wealthManagementEnabled,
        institutionalLendingEnabled,
        offshoreBankEnabled,
      },
      error,
      inProgress,
      formUpdated,
    },
    { setFieldSimple, setRadioField, handleSubmit },
  ] = useForm(
    config?.businessSetup
      ? {
          ...config.businessSetup,
        }
      : {
          homeLoansEnabled: true,
          businessLoansEnabled: true,
          depositsEnabled: true,
          creditCardsEnabled: true,
          wealthManagementEnabled: false,
          institutionalLendingEnabled: false,
          offshoreBankEnabled: false,
        },
  );

  const onSubmit = useCallback(async () => {
    if (config) {
      const payload: API.SimulationUpdateRequest = {
        businessSetup: {
          homeLoansEnabled,
          businessLoansEnabled,
          depositsEnabled,
          creditCardsEnabled,
          wealthManagementEnabled,
          institutionalLendingEnabled,
          offshoreBankEnabled,
        },
      };

      const response = await API.editSimulation(config.id, payload);

      updateConfig(response);
      return response;
    }
  }, [
    config,
    homeLoansEnabled,
    businessLoansEnabled,
    depositsEnabled,
    creditCardsEnabled,
    wealthManagementEnabled,
    institutionalLendingEnabled,
    offshoreBankEnabled,
    updateConfig,
  ]);

  useEffect(() => {
    assignOnSubmitFunction(handleSubmit(onSubmit));
  }, [assignOnSubmitFunction, handleSubmit, onSubmit]);

  useEffect(() => {
    updateFormStatus({
      inProgress: inProgress,
      formUpdated: formUpdated,
    });
  }, [updateFormStatus, inProgress, formUpdated]);

  const onRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRadioField([
        "creditCardsEnabled",
        "wealthManagementEnabled",
        "institutionalLendingEnabled",
        "offshoreBankEnabled",
      ])(e.target.value);
    },
    [setRadioField],
  );

  return (
    <div className="step-container">
      <VerticalGroup spread full>
        <VerticalGroup wide>
          <Banner type="error" active={!!error} message={error?.message} />
          <Form id="simulation-add-form">
            <h3>Business Settings</h3>
            <Text size="sm" bold>
              Compulsory Businesses
            </Text>
            <Checkbox
              disabled
              name="home-loans"
              checked={homeLoansEnabled}
              onChange={(e) =>
                setFieldSimple("homeLoansEnabled")(e.target.checked)
              }
              label="Home Loans"
            />
            <Checkbox
              disabled
              name="business-loans"
              checked={businessLoansEnabled}
              onChange={(e) =>
                setFieldSimple("businessLoansEnabled")(e.target.checked)
              }
              label="Business Loans"
            />
            <Checkbox
              disabled
              name="deposits"
              checked={depositsEnabled}
              onChange={(e) =>
                setFieldSimple("depositsEnabled")(e.target.checked)
              }
              label="Deposits"
            />
            <Text size="sm" bold>
              Select the 4th Business
            </Text>
            <Radio
              name="extra-division"
              checked={creditCardsEnabled}
              value="creditCardsEnabled"
              onChange={onRadioChange}
              label="Credit Cards"
            />
            <Radio
              name="extra-division"
              checked={wealthManagementEnabled}
              value="wealthManagementEnabled"
              onChange={onRadioChange}
              label="Wealth Management"
            />
            <Radio
              name="extra-division"
              checked={institutionalLendingEnabled}
              value="institutionalLendingEnabled"
              onChange={onRadioChange}
              label="Institutional Lending"
            />
            <Radio
              name="extra-division"
              checked={offshoreBankEnabled}
              value="offshoreBankEnabled"
              onChange={onRadioChange}
              label="Offshore Bank"
            />
          </Form>
        </VerticalGroup>
      </VerticalGroup>
    </div>
  );
};

export { default as BusinessDisplay } from "./Display";
export default Business;
